import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileText, Users, Settings, Eye, Upload, RefreshCw, Loader2, ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getTierLabel } from "@/lib/tierUtils";
import { formatDateString } from "@/lib/utils";
import type { UserTier } from "@/lib/tierUtils";
import { useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getSignedUrl, getBucketFromPath } from "@/lib/storageUtils";
import {
  uploadFileToStorage,
  MAX_UPLOAD_BYTES,
  FILE_TOO_LARGE_MESSAGE,
} from "@/lib/resumableUpload";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Document, Page, pdfjs } from "react-pdf";

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Anything larger than this opens in a new tab instead of the inline reader.
// Big scans reliably kill the embedded viewer on phones.
const INLINE_PREVIEW_LIMIT = 10 * 1024 * 1024;

interface ConstitutionDocument {
  id: string;
  file_url: string;
  document_name: string;
  file_path?: string | null;
  file_size?: number | null;
}

interface UNAHeaderCardProps {
  unaName: string;
  constitutionDate?: string;
  treasurySecretaryName: string;
  tier: UserTier;
  constitutionDocument?: ConstitutionDocument | null;
  onConstitutionUpload?: () => void;
  userId?: string;
  primaryOfficerRole?: string;
}

export const UNAHeaderCard = ({
  unaName,
  constitutionDate,
  treasurySecretaryName,
  tier,
  constitutionDocument,
  onConstitutionUpload,
  userId,
  primaryOfficerRole,
}: UNAHeaderCardProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadPercent, setUploadPercent] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [preparingPreview, setPreparingPreview] = useState(false);


  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !userId) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: "Invalid file type",
        description: "Please upload a PDF or image file (JPG, PNG)",
        variant: "destructive",
      });
      return;
    }

    // Validate file size (max 100MB)
    if (file.size > MAX_UPLOAD_BYTES) {
      toast({
        title: "That file is too big",
        description: FILE_TOO_LARGE_MESSAGE,
        variant: "destructive",
      });
      return;
    }

    // Duplicate guard. If the same file was stored a moment ago, say so
    // instead of quietly keeping another copy. Members re-upload when viewing
    // fails, and that is how three copies of one scan piled up.
    if (
      constitutionDocument?.document_name === file.name &&
      constitutionDocument?.file_size === file.size &&
      constitutionDocument?.file_path
    ) {
      toast({
        title: "You already saved this file",
        description:
          "This looks like the same file you saved a moment ago. Use View Constitution to open it. Choose a different file if you meant to replace it.",
      });
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setUploading(true);
    setUploadPercent(0);
    try {
      // Upload to storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${userId}/constitution-${Date.now()}.${fileExt}`;

      await uploadFileToStorage({
        bucket: 'documents',
        path: fileName,
        file,
        upsert: true,
        onProgress: setUploadPercent,
      });

      // The documents bucket is private. Store the storage path, never a
      // public URL: a public URL for a private bucket is refused on read,
      // which is exactly why viewing used to fail.
      const previousPath = constitutionDocument?.file_path || null;

      if (constitutionDocument?.id) {
        // Update existing record. file_path must move with file_url, or the
        // record ends up pointing at two different objects.
        const { error: updateError } = await supabase
          .from('user_journey_documents')
          .update({
            file_url: fileName,
            file_path: fileName,
            document_name: file.name,
            mime_type: file.type,
            file_size: file.size,
            updated_at: new Date().toISOString(),
          })
          .eq('id', constitutionDocument.id);

        if (updateError) throw updateError;

        // Clear the copy this one replaced so duplicates do not accumulate.
        if (previousPath && previousPath !== fileName) {
          await supabase.storage.from('documents').remove([previousPath]);
        }
      } else {
        // Insert new record
        const { error: insertError } = await supabase
          .from('user_journey_documents')
          .insert({
            user_id: userId,
            document_type: 'constitution',
            file_url: fileName,
            file_path: fileName,
            document_name: file.name,
            mime_type: file.type,
            file_size: file.size,
          });

        if (insertError) throw insertError;
      }

      toast({
        title: "Constitution uploaded",
        description: "Your constitution has been saved securely.",
      });

      onConstitutionUpload?.();
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: "Upload failed",
        description: "There was an error uploading your constitution. Please try again.",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
      setUploadPercent(0);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPageNumber(1);
  };

  const isPdf = constitutionDocument?.document_name?.toLowerCase().endsWith('.pdf') ||
                (constitutionDocument?.file_path || constitutionDocument?.file_url || '')
                  .toLowerCase()
                  .includes('.pdf');

  const isLargeFile =
    (constitutionDocument?.file_size ?? 0) > INLINE_PREVIEW_LIMIT;

  // Viewing always resolves a fresh signed link at click time, so nothing
  // stale or expired is ever held in the page.
  const handleView = async () => {
    if (!constitutionDocument) return;
    const path = constitutionDocument.file_path || constitutionDocument.file_url;
    if (!path) {
      toast({
        title: "No file saved yet",
        description: "Upload your Constitution and it will appear here.",
        variant: "destructive",
      });
      return;
    }

    setPreparingPreview(true);
    try {
      const signed = await getSignedUrl(getBucketFromPath(path), path, 3600);
      if (!signed) throw new Error('Could not create a link to this file');

      if (isLargeFile) {
        // Large scans do not survive the inline reader, so open the file
        // itself in a new tab and let the device handle it.
        window.open(signed, '_blank', 'noopener,noreferrer');
        return;
      }

      setPreviewUrl(signed);
      setPageNumber(1);
      setNumPages(null);
      setPreviewOpen(true);
    } catch (error) {
      console.error('Constitution view failed:', error);
      toast({
        title: "Could not open your Constitution",
        description: "Your file is safe. Please try again in a moment.",
        variant: "destructive",
      });
    } finally {
      setPreparingPreview(false);
    }
  };


  return (
    <>
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
            <div className="space-y-2 flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-3xl md:text-4xl font-bold">{unaName}</h1>
                <Badge variant="secondary" className="text-sm">
                  {getTierLabel(tier)}
                </Badge>
              </div>
              <div className="space-y-1 text-muted-foreground">
              {constitutionDate && (
                  <p className="text-sm">
                    Constituted: {formatDateString(constitutionDate)}
                  </p>
                )}
                <p className="text-sm">{primaryOfficerRole || 'Secretary Treasurer'}: {treasurySecretaryName}</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {/* Constitution button - conditional based on upload status */}
              {constitutionDocument ? (
                <div className="flex items-center">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleView}
                    disabled={preparingPreview}
                    className="rounded-r-none border-r-0"
                  >
                    {preparingPreview ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : isLargeFile ? (
                      <ExternalLink className="h-4 w-4 mr-2" />
                    ) : (
                      <Eye className="h-4 w-4 mr-2" />
                    )}
                    View Constitution
                  </Button>

                  <Button
                    variant="outline"
                    size="icon"
                    className="rounded-l-none h-9 w-9"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    title="Replace with new upload"
                  >
                    {uploading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  {uploading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Upload Constitution
                    </>
                  )}
                </Button>
              )}
              
              <Button variant="outline" size="sm" onClick={() => navigate('/una-members')}>
                <Users className="h-4 w-4 mr-2" />
                View Members
              </Button>
              <Button variant="outline" size="sm" onClick={() => navigate('/settings')}>
                <Settings className="h-4 w-4 mr-2" />
                UNA Settings
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept=".pdf,application/pdf,.jpg,.jpeg,.png,image/jpeg,image/png"
        className="hidden"
      />

      {/* Preview Dialog */}
      <Dialog
        open={previewOpen}
        onOpenChange={(open) => {
          setPreviewOpen(open);
          if (!open) setPreviewUrl(null);
        }}
      >
        <DialogContent className="max-w-4xl max-h-[90dvh] overflow-auto">
          <DialogHeader>
            <DialogTitle>
              {constitutionDocument?.document_name || 'Constitution'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex flex-col items-center">
            {!previewUrl ? (
              <div className="flex items-center justify-center p-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : isPdf ? (
              <>
                <Document
                  file={previewUrl}
                  onLoadSuccess={onDocumentLoadSuccess}
                  loading={
                    <div className="flex items-center justify-center p-8">
                      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    </div>
                  }
                  error={
                    <div className="text-center p-8 space-y-3">
                      <p className="text-muted-foreground text-sm">
                        This file is too large to show here. Your file is safe.
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          window.open(previewUrl, '_blank', 'noopener,noreferrer')
                        }
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Open in a new tab
                      </Button>
                    </div>
                  }
                >
                  <Page 
                    pageNumber={pageNumber} 
                    width={Math.min(800, window.innerWidth - 100)}
                    renderTextLayer={true}
                    renderAnnotationLayer={true}
                  />
                </Document>
                
                {numPages && numPages > 1 && (
                  <div className="flex items-center gap-4 mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPageNumber(p => Math.max(1, p - 1))}
                      disabled={pageNumber <= 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-muted-foreground">
                      Page {pageNumber} of {numPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPageNumber(p => Math.min(numPages, p + 1))}
                      disabled={pageNumber >= numPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <img 
                src={previewUrl} 
                alt="Constitution" 
                className="max-w-full max-h-[70vh] object-contain"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

    </>
  );
};
